import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { resetAction } from "../../../App/state/actions";
import {
  loadCurrentSeasonDetails,
  loadProgramDetail,
  loadRecordingProgramDetail,
  loadCurrentSeriesDetails,
  loadCurrentSeasonEntitlements,
} from "../state/actions";
import constants from "../../../shared/constants";
import middleware from "../../../shared/middleware";
import { getAVSKeyArtImage } from "../../../shared/utils/image";
import {
  LOAD_CURRENT_SERIES_DETAILS,
  LOAD_PLAYBACK_SEASON_DATA,
  LOAD_CURRENT_SEASON_DETAILS,
  LOAD_CURRENT_PROGRAM_DETAILS,
  LOAD_CURRENT_SEASON_ENTITLEMENTS,
} from "../state/actions";

const { getNextProgram } = middleware;

const { IMAGES, PAGE_CONTENT_ITEM_TYPES } = constants;

export const useStaticPlayerEffects = (
  appProvider,
  contentType,
  isLivePlayer,
  isVodPlayer,
  isRecordingPlayer,
  stopDeltaTime,
  playbackID,
  isTVShow,
  programContent,
  userInactivityTimer,
  setShowStillWatchingPrompt,
  playbackType,
  onEpisodeItemClick,
  cancelTokenSource,
  bingeWatchEpisodeCounter,
  setBingeWatchEpisodeCounter,
  currentEpisodeId
) => {
  const seasonData = useSelector(({ onDemandPlayer }) => onDemandPlayer.seasonDetails);
  const dispatch = useDispatch();
  const [season, setSeason] = useState(null);
  const [nextProgramInfo, setNextProgramInfo] = useState({});
  const prevSeason = useRef(null); // Persist previous season state to prevent additional API calls.
  const prevSeriesId = useRef(null); // Persist previous seriesId to prevent additional API calls.

  useEffect(() => {
    if (!isLivePlayer) {
      if (isVodPlayer && playbackID && contentType)
        dispatch(loadProgramDetail(appProvider, playbackID, contentType?.toLowerCase()));
      else dispatch(loadRecordingProgramDetail(appProvider, playbackID, contentType, stopDeltaTime));
      return () => {
        // Clean up the Redux states on player page unmount, required for both VOD and Recording
        dispatch(resetAction(LOAD_CURRENT_PROGRAM_DETAILS, "content"));
        dispatch(resetAction(LOAD_CURRENT_SERIES_DETAILS, "content"));
        dispatch(resetAction(LOAD_PLAYBACK_SEASON_DATA, "content"));
        dispatch(resetAction(LOAD_CURRENT_SEASON_DETAILS, "content"));
        dispatch(resetAction(LOAD_CURRENT_SEASON_ENTITLEMENTS, "content"));
        isTVShow.current = false;
      };
    }
  }, [appProvider, contentType, isLivePlayer, isTVShow, isVodPlayer, playbackID, dispatch, stopDeltaTime]);

  useEffect(() => {
    if (!isLivePlayer && programContent && programContent.length) {
      const extendedMetadata = programContent[0]?.metadata?.extendedMetadata ?? null;
      const seriesId = extendedMetadata?.dlum?.series?.uaId || extendedMetadata?.dlum?.uaGroupId;
      if (seriesId && seriesId !== prevSeriesId.current) {
        prevSeriesId.current = seriesId;
        dispatch(loadCurrentSeriesDetails(appProvider, PAGE_CONTENT_ITEM_TYPES.tvShow, seriesId));
      }
    }
  }, [appProvider, isLivePlayer, dispatch, prevSeriesId, programContent]);

  useEffect(() => {
    if (!isLivePlayer && seasonData && Array.isArray(seasonData)) {
      const currentSeason = seasonData.find(
        (season) => season?.metadata?.season === parseInt(programContent?.[0]?.metadata?.season)
      );
      if (currentSeason) {
        setSeason(currentSeason);
      }
    }
  }, [isLivePlayer, programContent, seasonData]);

  useEffect(() => {
    if (!isLivePlayer && season?.id && season?.metadata?.season) {
      if (season.id !== prevSeason.current?.id && season.metadata?.season !== prevSeason.current?.metadata?.season) {
        dispatch(loadCurrentSeasonDetails(appProvider, season));
        dispatch(loadCurrentSeasonEntitlements(appProvider, `${season.id}?season=${season.metadata?.season}`));
        prevSeason.current = season;
      }
    }
  }, [appProvider, isLivePlayer, dispatch, season]);

  useEffect(() => {
    if (!isLivePlayer && appProvider.config?.player_inactivity && bingeWatchEpisodeCounter > 0) {
      const { vod_episode_limit, vod_timeout } = appProvider.config.player_inactivity;
      if (bingeWatchEpisodeCounter === vod_episode_limit) {
        // start a timer to show the still watching prompt after a certain number of episodes
        userInactivityTimer.current = setTimeout(() => {
          setShowStillWatchingPrompt(true);
        }, vod_timeout * 1000);
        setBingeWatchEpisodeCounter(0);
      }
    }
  }, [
    appProvider,
    bingeWatchEpisodeCounter,
    isLivePlayer,
    setBingeWatchEpisodeCounter,
    setShowStillWatchingPrompt,
    userInactivityTimer,
  ]);

  useEffect(() => {
    if (!isLivePlayer && playbackType && playbackType === "episode" && currentEpisodeId && !isRecordingPlayer) {
      getNextProgram(appProvider, currentEpisodeId, cancelTokenSource.current)
        .then((res) => {
          const nextProgram = res?.containers?.[0];
          setNextProgramInfo({
            nextProgram,
            nextEpisodePoster: nextProgram && getAVSKeyArtImage(nextProgram.metadata, IMAGES.ASPECT_RATIOS.DIM_9x16),
            loadNextProgram: (isAutoplay = false, playNextEpisodeCallback) =>
              onEpisodeItemClick(nextProgram, true, isAutoplay, null, playNextEpisodeCallback),
          });
        })
        .catch((e) => {
          console.error("Failed to get next program for current episode, error = ", e);
          setNextProgramInfo({});
        });
    }
  }, [
    appProvider,
    currentEpisodeId,
    isLivePlayer,
    onEpisodeItemClick,
    playbackType,
    isRecordingPlayer,
    cancelTokenSource,
  ]);

  return {
    season,
    setBingeWatchEpisodeCounter,
    nextProgramInfo,
  };
};
